<template>
  <div class="container page">
    <div class="header">
      <van-nav-bar :title="$t('feedback.title')" class="nav-bar">
        <template #left>
          <van-icon name="arrow-left" color="#fff" @click="back" />
        </template>
      </van-nav-bar>
    </div>

    <div class="content">
      <div class="formBox">
        <van-field
          v-model="form.title"
          :label="$t('feedback.title_label')"
          :placeholder="$t('feedback.title_placeholder')"
        />

        <van-field
          v-model="form.content"
          :label="$t('feedback.content_label')"
          type="textarea"
          :autosize="{ minHeight: 72, maxHeight: 160 }"
          :placeholder="$t('feedback.content_placeholder')"
        />

        <van-field
          v-model="form.author"
          :label="$t('feedback.author_label')"
          :placeholder="$t('feedback.author_placeholder')"
        />

        <div class="uploaderRow">
          <div class="uploaderTitle">{{ $t('feedback.images_label') }}</div>
          <van-uploader
            v-model="form.images"
            :after-read="afterRead"
            multiple
            :max-count="3"
          />
        </div>

        <van-button type="primary" block @click="submit">
          {{ $t('feedback.submit_button') }}
        </van-button>
      </div>

      <van-pull-refresh v-model="isLoading" @refresh="onRefresh">
        <div
          class="listItem pending"
          v-for="(item, index) in pendingList"
          :key="'pending-' + index"
        >
          <div class="listTitle">{{ item.title }}</div>
          <div class="statusTag">{{ $t('feedback.pending') }}</div>
          <div class="listContent">
            <p>{{ item.content }}</p>
            <div class="imgBox" v-if="item.images && item.images.length">
              <img
                v-for="(img, imgIndex) in item.images"
                :key="imgIndex"
                :src="getImg(img)"
                @click="preview(item.images, imgIndex)"
              />
            </div>
          </div>
          <div class="listTime">
            <div class="listTimeText">{{ formatMeta(item) }}</div>
          </div>
        </div>

        <div
          class="listItem"
          v-for="(item, index) in list"
          :key="'list-' + index"
        >
          <div class="listTitle">{{ item.title }}</div>
          <div class="listContent">
            <p>{{ item.content }}</p>
            <div class="imgBox" v-if="item.images && item.images.length">
              <img
                v-for="(img, imgIndex) in item.images"
                :key="imgIndex"
                :src="getImg(img)"
                @click="preview(item.images, imgIndex)"
              />
            </div>
          </div>
          <div class="listTime">
            <div class="listTimeText">{{ formatMeta(item) }}</div>
          </div>
        </div>

        <van-empty
          v-if="!pendingList.length && !list.length && !loading"
          :description="$t('feedback.empty')"
        />

        <div
          v-if="list.length"
          class="loadMore"
          @click="loadMore"
        >
          {{ finished ? $t('feedback.no_more') : $t('feedback.load_more') }}
        </div>
      </van-pull-refresh>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { Base64 } from 'js-base64';
import { ImagePreview } from 'vant';

const API_BASE_URL = '/api';
const FILE_BASE_URL = 'https://admin.pairsvip.jp';

export default {
  data() {
    return {
      isLoading: false,
      loading: false,
      finished: false,
      page: 1,
      pageSize: 10,
      list: [],
      pendingList: [],
      form: {
        title: '',
        content: '',
        author: '',
        images: []
      }
    };
  },
  methods: {
    back() {
      window.history.back();
    },
    resetForm() {
      this.form = {
        title: '',
        content: '',
        author: '',
        images: []
      };
    },
    normalizeList(list = []) {
      return list.map((item) => ({
        ...item,
        create_time: this.formatTime(item.create_time),
        images: Array.isArray(item.images)
          ? item.images
          : item.images
          ? String(item.images).split(',').filter(Boolean)
          : []
      }));
    },
    getRequestHeaders() {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('lang') || 'zh';
      const headers = {
        lang
      };

      if (token) {
        headers.token = Base64.encode(token);
      }

      return headers;
    },
    decodeResponseData(result) {
      if (!result || !result.data) {
        return {};
      }

      if (typeof result.data === 'string') {
        try {
          return JSON.parse(Base64.decode(result.data));
        } catch (error) {
          return {};
        }
      }

      return result.data;
    },
    formatTime(value) {
      if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') {
        return '';
      }

      return value;
    },
    formatMeta(item) {
      const author = item.author || this.$t('feedback.anonymous');
      const time = this.formatTime(item.create_time);
      return time ? `${author} · ${time}` : author;
    },
    async afterRead(file) {
      const files = Array.isArray(file) ? file : [file];

      for (const item of files) {
        item.status = 'uploading';
        item.message = this.$t('feedback.upload_ing');

        const formData = new FormData();
        formData.append('file', item.file);

        try {
          const response = await axios.post(
            `${API_BASE_URL}/feedback/upload`,
            formData,
            {
              headers: this.getRequestHeaders(),
              withCredentials: false
            }
          );

          const result = response.data || {};
          const data = this.decodeResponseData(result);

          if (result.code === 200 && data.url) {
            item.url = `${FILE_BASE_URL}${data.url}`;
            item.status = 'done';
            item.message = '';
          } else {
            item.status = 'failed';
            item.message = this.$t('feedback.upload_fail');
            this.$toast(result.msg || this.$t('feedback.upload_fail'));
          }
        } catch (error) {
          item.status = 'failed';
          item.message = this.$t('feedback.upload_fail');
          this.$toast(this.$t('feedback.upload_fail'));
        }
      }
    },
    submit() {
      if (!this.form.title.trim() || !this.form.content.trim()) {
        this.$toast(this.$t('feedback.form_incomplete'));
        return;
      }

      this.$http({
        method: 'post',
        url: 'feedback_add',
        data: {
          title: this.form.title.trim(),
          content: this.form.content.trim(),
          author: this.form.author.trim(),
          images: this.form.images
            .filter((item) => item.status === 'done' && item.url)
            .map((item) => item.url.replace(FILE_BASE_URL, ''))
            .join(',')
        }
      }).then((res) => {
        if (res.code === 200) {
          const message = res.data && res.data.msg ? res.data.msg : this.$t('feedback.submit_success');
          this.$toast(message);
          this.resetForm();
          this.page = 1;
          this.finished = false;
          this.fetchList(true);
          return;
        }

        this.$toast(res.msg || this.$t('feedback.submit_fail'));
      });
    },
    fetchList(reset = false) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      const page = reset ? 1 : this.page;

      this.$http({
        method: 'get',
        url: 'feedback_list',
        data: { page }
      }).then((res) => {
        if (res.code !== 200) {
          this.$toast(res.msg || this.$t('feedback.fetch_fail'));
          return;
        }

        const data = res.data || {};
        const publicList = this.normalizeList(data.list || []);
        const pendingList = this.normalizeList(data.pending || []);

        this.pendingList = pendingList;
        this.list = reset ? publicList : this.list.concat(publicList);
        this.finished = publicList.length < this.pageSize;
        this.page = page + 1;
      }).finally(() => {
        this.loading = false;
        this.isLoading = false;
      });
    },
    onRefresh() {
      this.page = 1;
      this.finished = false;
      this.fetchList(true);
    },
    loadMore() {
      if (this.finished) {
        return;
      }
      this.fetchList(false);
    },
    getImg(path) {
      if (!path) {
        return '';
      }
      if (String(path).startsWith('http')) {
        return path;
      }
      return `${FILE_BASE_URL}${path}`;
    },
    preview(images, index) {
      ImagePreview({
        images: images.map((item) => this.getImg(item)),
        startPosition: index
      });
    }
  },
  created() {
    this.fetchList(true);
  }
};
</script>

<style lang="less" scoped>
@import "../../assets/css/base.css";

::v-deep .van-pull-refresh__track .van-pull-refresh__head * {
  color: #000000;
  font-size: 35px;
}

.container .content {
  height: calc(100% - 20px);
  overflow: auto;
}

.formBox {
  background: #fff;
  padding: 14px 14px 18px;
  margin-bottom: 20px;
}

.uploaderRow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0 6px;
}

.uploaderTitle {
  flex: 0 0 78px;
  font-size: 24px;
  line-height: 1.3;
  white-space: nowrap;
  color: #333;
  margin: 0;
}

.uploaderRow ::v-deep .van-uploader {
  flex: 1;
  display: flex;
  justify-content: center;
  min-width: 0;
}

.listItem {
  margin-bottom: 20px;
  padding: 20px 20px 0;
  position: relative;
  color: #000;
  background-color: #fff;
}

.listTitle {
  font-size: 38px;
  font-weight: 600;
  padding-right: 120px;
}

.statusTag {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 22px;
  color: #f08a24;
  background: #fff4e8;
  padding: 6px 12px;
  border-radius: 20px;
}

.listContent {
  border-bottom: 2px solid #f2f2f5;
  padding: 12px 0;
  font-size: 26px;
  line-height: 1.7;
}

.listContent p {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.listTime {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 70px;
}

.listTimeText {
  color: #656566;
  font-size: 30px;
}

.imgBox {
  display: flex;
  flex-wrap: wrap;
  margin-top: 12px;
}

.imgBox img {
  width: 30%;
  height: 180px;
  margin: 10px 10px 0 0;
  border-radius: 8px;
  object-fit: cover;
}

.pending {
  background: #fcfcfc;
}

.loadMore {
  padding: 24px 0 40px;
  text-align: center;
  color: #666;
  font-size: 28px;
}

.container {
  font-size: 30px;
}

::v-deep .van-field__label {
  display: flex;
  align-items: center;
  min-height: 36px;
  margin-right: 10px;
  font-size: 24px !important;
  line-height: 1.3 !important;
  color: #333;
}

::v-deep .van-field__control {
  display: flex;
  align-items: center;
  min-height: 36px;
  font-size: 23px !important;
  line-height: 1.5 !important;
  padding: 4px 0 !important;
  color: #000;
}

::v-deep input::placeholder,
::v-deep textarea::placeholder {
  font-size: 22px;
  color: #999;
  line-height: 1.5 !important;
}

::v-deep .van-cell {
  display: flex;
  align-items: center;
  padding: 10px 8px;
  min-height: 56px;
}

::v-deep .van-field__body {
  display: flex;
  align-items: center;
  min-height: 36px;
}

::v-deep textarea.van-field__control {
  display: block;
  min-height: 54px;
  line-height: 1.4 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

::v-deep .van-nav-bar__title {
  font-size: 36px;
  font-weight: 600;
}

.van-button {
  font-size: 28px !important;
  height: 72px;
  border-radius: 12px;
  margin-top: 14px;
}

::v-deep .van-uploader__upload {
  width: 88px;
  height: 88px;
}

::v-deep .van-uploader__preview,
::v-deep .van-uploader__preview-image {
  width: 88px !important;
  height: 88px !important;
}
</style>
